import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatedRouter, TranslationService } from '@core-services';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Language, Path } from '@shared-enums';
import { sunbedCartCounterSelector } from '@store-sunbed-selectors';

@Component({
  selector: 'app-sunbed-header',
  standalone: true,
  imports: [
    TranslateModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatButtonModule,
    MatInputModule,
    CommonModule,
    MatBadgeModule,
    MatFormFieldModule,
  ],
  templateUrl: './sunbed-header.component.html',
  styleUrl: './sunbed-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SunbedHeaderComponent implements OnInit {
  //#region Dependencies

  private router = inject(TranslatedRouter);
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);
  private translationService = inject(TranslationService);

  //#endregion

  //#region Properties

  path = Path;
  language = Language;
  languageForm: FormGroup;

  cartItemsCount = signal<string>('');

  //#endregion

  //#region Lifecycle hooks

  ngOnInit(): void {
    this.initLanguage();
    this.initLanguageChangeSubscription();
    this.initCartItemsCount();
  }

  //#endregion

  //#region Init

  private initCartItemsCount(): void {
    this.store
      .select(sunbedCartCounterSelector)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(counter => {
        this.cartItemsCount.set(counter > 0 ? `${counter}` : '');
      });
  }

  private initLanguage(): void {
    this.languageForm = new FormGroup({
      language: new FormControl(this.translationService.getCurrentLanguage(), []),
    });
  }

  private initLanguageChangeSubscription(): void {
    this.languageForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.handleLanguageChange);
  }

  //#endregion

  //#region UI Methods

  onHome(): void {
    this.router.navigateByUrl(Path.Root);
  }

  onOpenShoppingCart(): void {
    this.router.navigateByUrl(`${Path.SunbedsLanding}/${Path.SunbedCart}`);
  }

  //#endregion

  //#region Handlers

  private handleLanguageChange = (change: { language: Language }): void => {
    this.translationService.setCurrentLanguage(change.language);
  };

  //#endregion
}
